import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { registerUser } from "../../../redux/actions/user.actions";
import { fetchAllDealerships } from "../../../redux/actions/dealership.actions";
import RootState from "../../../redux/states/root.state";
import { AppDispatch } from "../../../redux/store";
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from "../../../components/common/Icons/Icons";
import AuthAlert from "./AuthAlert";
import PasswordInput from "./PasswordInput";
// Dealership search dropdown styles, shared with Settings > Edit Profile
import "../../Settings/EditProfile/EditProfile.css";

const RegistrationForm: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  // Dealership selection state
  const [dealershipInput, setDealershipInput] = useState("");
  const [selectedDealershipId, setSelectedDealershipId] = useState("");
  const [dealershipPage, setDealershipPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch<AppDispatch>();

  const registerError = useSelector((state: RootState) => state.user.error);
  const loading = useSelector((state: RootState) => state.user.loading);
  const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector(
    (state: RootState) => state.dealerships
  );

  // Fetch dealerships when input or page changes
  useEffect(() => {
    if (dropdownOpen) {
      const query = `name=${encodeURIComponent(dealershipInput)}&page=${dealershipPage}`;
      dispatch(fetchAllDealerships(query));
    }
  }, [dealershipInput, dealershipPage, dropdownOpen, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".dealership-dropdown")
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    await dispatch(
      registerUser({
        FirstName: firstName,
        LastName: lastName,
        UserName: username,
        Email: email,
        DealerID: `${selectedDealershipId}`,
        Password: password,
        PasswordConfirmation: passwordConfirmation,
      })
    );
    // AuthRoute will handle the redirect after successful registration
  };

  const dealershipOptions = (dealerships || []).filter((d: any) => d && d.id && d.name);
  const hasNextPage = !!(dealershipsMeta && dealershipPage < dealershipsMeta.total_pages);

  return (
    <form className="auth-form" onSubmit={handleRegister} noValidate>
      <div className="auth-row">
        <div className="field">
          <label className="field-label" htmlFor="first_name">First name<span className="auth-required" aria-hidden="true">*</span></label>
          <input
            id="first_name"
            name="first_name"
            className="input"
            autoComplete="given-name"
            autoFocus
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="last_name">Last name<span className="auth-required" aria-hidden="true">*</span></label>
          <input
            id="last_name"
            name="last_name"
            className="input"
            autoComplete="family-name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="email">Email address<span className="auth-required" aria-hidden="true">*</span></label>
        <input
          id="email"
          name="email"
          className="input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="username">Username<span className="auth-required" aria-hidden="true">*</span></label>
        <input
          id="username"
          name="username"
          className="input"
          autoComplete="off"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>

      {/* Dealership selection */}
      <div className="field ep-dealer">
        <label className="field-label" htmlFor="dealership">Preferred dealership<span className="auth-required" aria-hidden="true">*</span></label>
        <input
          id="dealership"
          ref={inputRef}
          className="input"
          type="search"
          placeholder="Search dealerships"
          required
          value={dealershipInput}
          onFocus={() => setDropdownOpen(true)}
          onChange={(e) => {
            const value = e.target.value;
            setDealershipInput(value);
            setDealershipPage(1);

            // If user clears the input, clear the selected dealership and close dropdown
            if (value === "") {
              setSelectedDealershipId("");
              setDropdownOpen(false);
            } else {
              setDropdownOpen(true);
            }
          }}
          role="combobox"
          aria-expanded={dropdownOpen}
          aria-controls="dealership-list"
          autoComplete="off"
        />

        {dropdownOpen && (
          <div className="dealership-dropdown ep-dealer-dropdown">
            {dealershipsLoading ? (
              <div className="ep-dealer-empty">Loading…</div>
            ) : (
              <>
                {dealershipOptions.length === 0 ? (
                  <div className="ep-dealer-empty">No dealerships found</div>
                ) : (
                  <ul className="ep-dealer-list" id="dealership-list" role="listbox">
                    {dealershipOptions.map((d: any) => (
                      <li key={d.id} role="option" aria-selected={selectedDealershipId === d.id}>
                        <button
                          type="button"
                          className={`ep-dealer-option ${selectedDealershipId === d.id ? "selected" : ""}`}
                          onClick={() => {
                            setDealershipInput(d.name);
                            setSelectedDealershipId(d.id);
                            setDropdownOpen(false);
                          }}
                        >
                          {d.name}
                          {selectedDealershipId === d.id && <CheckCircleIcon />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="ep-dealer-pager">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={dealershipPage <= 1}
                    onClick={() => dealershipPage > 1 && setDealershipPage(dealershipPage - 1)}
                  >
                    <ChevronLeftIcon /> Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={!hasNextPage}
                    onClick={() => hasNextPage && setDealershipPage(dealershipPage + 1)}
                  >
                    Next <ChevronRightIcon />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="field">
        <label className="field-label" htmlFor="password">Password<span className="auth-required" aria-hidden="true">*</span></label>
        <PasswordInput
          id="password"
          name="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="password_confirmation">Re-type password<span className="auth-required" aria-hidden="true">*</span></label>
        <PasswordInput
          id="password_confirmation"
          name="password_confirmation"
          value={passwordConfirmation}
          onChange={setPasswordConfirmation}
          autoComplete="new-password"
          required
        />
      </div>

      {registerError && <AuthAlert kind="error" message={registerError} />}

      <div className="auth-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? <span className="spinner auth-btn-spinner" aria-label="Creating account" /> : "Create account"}
        </button>
      </div>

      <p className="auth-footer">
        Already have an account?
        <Link to="/login" className="auth-link">Log in</Link>
      </p>
    </form>
  );
};

export default RegistrationForm;
